import React, { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Cpu, Brain, Zap, CheckCircle, Play, Loader, Terminal, FileText, Cloud, Key, Monitor, AlertTriangle, Clock, X } from 'lucide-react';
import api from '../api';
import ReportModal from './ReportModal';
import Framework5D from './Framework5D';

interface CrossDomainIncidentDetailProps {
  incident: CrossDomainIncident;
  onBack: () => void;
  onRemediated?: (id: string) => void;
}

export interface CrossDomainIncident {
  id: string;
  scenario: {
    id: string;
    name: string;
    description: string;
  };
  context: {
    attackerIP: string;
    targetUser: string;
    instanceId: string;
    bucketName: string;
    hostname: string;
    deviceId: string;
    country: string;
    region: string;
    failedAttempts: number;
  };
  threats: {
    okta: ThreatDetail;
    aws: ThreatDetail;
    crowdstrike: ThreatDetail;
  };
  correlation: {
    sharedIndicators: string[];
    attackerIP: string;
    targetUser: string;
    timespan: string;
    confidence: number;
  };
  mitre: {
    tactics: string[];
    techniques: string[];
    severity: string;
  };
  remediation: {
    okta: RemediationPlan;
    aws: RemediationPlan;
    crowdstrike: RemediationPlan;
  };
  timestamp: string;
  status?: 'detected' | 'analyzing' | 'remediating' | 'remediated';
}

interface ThreatDetail {
  id: string;
  source: string;
  type: string;
  severity: string;
  title: string;
  description: string;
  timestamp: string;
  status: string;
  correlationId: string;
  indicators: Record<string, string>;
}

interface RemediationPlan {
  source: string;
  actions: RemediationAction[];
}

interface RemediationAction {
  step: number;
  action: string;
  command: string;
  description: string;
  keywords: string[];
}

interface TimelineEvent {
  id: string;
  timestamp: Date;
  source: 'okta' | 'aws' | 'crowdstrike' | 'arbitium';
  type: 'detection' | 'analysis' | 'remediation';
  title: string;
  description: string;
  command?: string;
  keywords?: string[];
  status: 'pending' | 'in-progress' | 'complete';
}

const CrossDomainIncidentDetail: React.FC<CrossDomainIncidentDetailProps> = ({ incident, onBack, onRemediated }) => {
  const [analyzing, setAnalyzing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [executed, setExecuted] = useState(incident.status === 'remediated');
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(-1);
  const [showReport, setShowReport] = useState(false);

  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'okta': return <Key className="w-4 h-4" />;
      case 'aws': return <Cloud className="w-4 h-4" />;
      case 'crowdstrike': return <Monitor className="w-4 h-4" />;
      case 'arbitium': return <Shield className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'okta': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
      case 'aws': return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
      case 'crowdstrike': return 'bg-red-500/20 text-red-400 border-red-500/30';
      case 'arbitium': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const highlightKeywords = (text: string, keywords?: string[]) => {
    if (!keywords || keywords.length === 0) return text;
    
    let result = text;
    keywords.forEach(keyword => {
      if (keyword && keyword.length > 2) {
        const regex = new RegExp(`(${keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        result = result.replace(regex, '<span class="text-yellow-400 font-semibold bg-yellow-400/10 px-1 rounded">$1</span>');
      }
    });
    return result;
  };

  const buildInitialTimeline = (): TimelineEvent[] => {
    const events: TimelineEvent[] = [];
    const baseTime = new Date(incident.timestamp);
    
    events.push({
      id: 'detect-okta',
      timestamp: new Date(baseTime.getTime()),
      source: 'okta',
      type: 'detection',
      title: incident.threats.okta.title,
      description: incident.threats.okta.description,
      keywords: [incident.context.attackerIP, incident.context.targetUser],
      status: 'complete'
    });
    
    events.push({
      id: 'detect-aws',
      timestamp: new Date(baseTime.getTime() + 120000),
      source: 'aws',
      type: 'detection',
      title: incident.threats.aws.title,
      description: incident.threats.aws.description,
      keywords: [incident.context.attackerIP, incident.context.instanceId, incident.context.targetUser.split('@')[0]],
      status: 'complete'
    });
    
    events.push({
      id: 'detect-cs',
      timestamp: new Date(baseTime.getTime() + 180000),
      source: 'crowdstrike',
      type: 'detection',
      title: incident.threats.crowdstrike.title,
      description: incident.threats.crowdstrike.description,
      keywords: [incident.context.hostname, incident.context.targetUser],
      status: 'complete'
    });
    
    events.push({
      id: 'correlate',
      timestamp: new Date(baseTime.getTime() + 200000),
      source: 'arbitium',
      type: 'analysis',
      title: 'Cross-Domain Correlation Detected',
      description: `Arbitium AI identified ${incident.correlation.confidence}% correlation across 3 platforms. Shared indicators: IP ${incident.context.attackerIP}, User ${incident.context.targetUser}`,
      keywords: [incident.context.attackerIP, incident.context.targetUser, `${incident.correlation.confidence}%`],
      status: 'complete'
    });
    
    return events;
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setTimeline(buildInitialTimeline());
    
    await new Promise(r => setTimeout(r, 1500));
    setAnalysisComplete(true);
    setAnalyzing(false);
  };

  const executeRemediation = async () => {
    setExecuting(true);
    
    const remediationEvents: TimelineEvent[] = [];
    const baseTime = Date.now();
    
    const allActions = [
      ...incident.remediation.okta.actions.map((a, i) => ({ ...a, source: 'okta' as const, delay: i * 800 })),
      ...incident.remediation.aws.actions.map((a, i) => ({ ...a, source: 'aws' as const, delay: i * 800 + 200 })),
      ...incident.remediation.crowdstrike.actions.map((a, i) => ({ ...a, source: 'crowdstrike' as const, delay: i * 800 + 400 }))
    ].sort((a, b) => a.delay - b.delay);
    
    for (let i = 0; i < allActions.length; i++) {
      const action = allActions[i];
      
      const newEvent: TimelineEvent = {
        id: `remediate-${action.source}-${action.step}`,
        timestamp: new Date(baseTime + action.delay),
        source: action.source,
        type: 'remediation',
        title: action.action,
        description: action.description,
        command: action.command,
        keywords: action.keywords,
        status: 'in-progress'
      };
      
      setTimeline(prev => [...prev, newEvent]);
      setCurrentEventIndex(prev => prev + 1);
      
      await new Promise(r => setTimeout(r, 600 + Math.random() * 400));
      
      setTimeline(prev => prev.map(e => 
        e.id === newEvent.id ? { ...e, status: 'complete' } : e
      ));
    }
    
    const finalEvent: TimelineEvent = {
      id: 'complete',
      timestamp: new Date(),
      source: 'arbitium',
      type: 'remediation',
      title: 'Cross-Domain Remediation Complete',
      description: `Successfully executed ${allActions.length} remediation actions across 3 platforms in 4.2 seconds. Threat contained.`,
      keywords: [`${allActions.length} actions`, '3 platforms', '4.2 seconds'],
      status: 'complete'
    };
    
    setTimeline(prev => [...prev, finalEvent]);
    setExecuted(true);
    setExecuting(false);
    
    if (onRemediated) {
      onRemediated(incident.id);
    }
  };

  const getSeverityColor = (severity: string) => {
    const colors: Record<string, string> = {
      critical: 'text-red-400 bg-red-400/10 border-red-400/30',
      high: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
      medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/30',
      low: 'text-green-400 bg-green-400/10 border-green-400/30',
    };
    return colors[severity] || colors.medium;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={onBack} className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-xl transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 py-1 text-sm font-bold rounded-xl border bg-gradient-to-r from-purple-500/20 to-cyan-500/20 text-white border-purple-500/30">
              🔗 CROSS-DOMAIN
            </span>
            <h1 className="text-2xl font-bold text-white">{incident.scenario.name}</h1>
            <span className={`px-3 py-1 text-sm font-medium rounded-xl border ${getSeverityColor(incident.mitre.severity)}`}>
              {incident.mitre.severity.toUpperCase()}
            </span>
            {executed && (
              <span className="px-3 py-1 text-sm font-medium rounded-xl border text-green-400 bg-green-400/10 border-green-400/30">
                ✓ CONTAINED
              </span>
            )}
          </div>
          <p className="text-gray-400 mt-1">{incident.scenario.description}</p>
        </div>
        <button
          onClick={() => setShowReport(true)}
          className="flex items-center gap-2 px-4 py-2 bg-arbitium-primary/20 hover:bg-arbitium-primary/30 text-arbitium-accent border border-arbitium-primary/30 rounded-xl transition-colors"
        >
          <FileText className="w-4 h-4" />
          Export Report
        </button>
      </div>

      {/* 5D Framework Analysis - Cross-Domain Specific */}
      <Framework5D 
        compact={true}
        activeThreat={{
          signalsScore: 0.96,
          actionsCount: (incident.remediation?.okta?.actions?.length || 0) + (incident.remediation?.aws?.actions?.length || 0) + (incident.remediation?.crowdstrike?.actions?.length || 0),
          contextHits: incident.mitre?.techniques?.length || 12,
          timeVelocity: 0.94,
          judgmentConfidence: incident.correlation?.confidence || 0.92
        }}
      />

      <div className="bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/30 rounded-xl p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="p-3 bg-purple-500/20 rounded-xl">
            <Shield className="w-6 h-6 text-purple-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Cross-Domain Correlation</h2>
            <p className="text-gray-400 text-sm">Arbitium AI detected coordinated attack across 3 platforms</p>
          </div>
          <div className="ml-auto text-right">
            <div className="text-3xl font-bold text-purple-400">{incident.correlation.confidence}%</div>
            <div className="text-sm text-gray-400">Correlation Confidence</div>
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-4">
          <div className="bg-arbitium-darker/50 rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-1">Attacker IP</div>
            <div className="text-white font-mono text-lg">{incident.context.attackerIP}</div>
            <div className="text-xs text-red-400 mt-1">🌍 {incident.context.country}</div>
          </div>
          <div className="bg-arbitium-darker/50 rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-1">Target User</div>
            <div className="text-white font-semibold">{incident.context.targetUser}</div>
            <div className="text-xs text-yellow-400 mt-1">⚠️ Credentials compromised</div>
          </div>
          <div className="bg-arbitium-darker/50 rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-1">Affected Systems</div>
            <div className="flex gap-2 mt-1">
              <span className="px-2 py-1 text-xs rounded bg-blue-500/20 text-blue-400">Okta</span>
              <span className="px-2 py-1 text-xs rounded bg-orange-500/20 text-orange-400">AWS</span>
              <span className="px-2 py-1 text-xs rounded bg-red-500/20 text-red-400">CrowdStrike</span>
            </div>
          </div>
          <div className="bg-arbitium-darker/50 rounded-xl p-4">
            <div className="text-sm text-gray-400 mb-1">MITRE ATT&CK</div>
            <div className="text-white text-sm">{incident.mitre.tactics.join(' → ')}</div>
            <div className="text-xs text-cyan-400 mt-1">{incident.mitre.techniques[0]}</div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-white">Detected Threats</h3>
          
          <div className="bg-arbitium-dark/50 border border-blue-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Key className="w-5 h-5 text-blue-400" />
              <span className="font-semibold text-blue-400">Okta</span>
              <span className={`ml-auto px-2 py-0.5 text-xs rounded ${getSeverityColor(incident.threats.okta.severity)}`}>
                {incident.threats.okta.severity}
              </span>
            </div>
            <div className="text-white font-medium mb-1">{incident.threats.okta.title}</div>
            <div 
              className="text-sm text-gray-400"
              dangerouslySetInnerHTML={{ __html: highlightKeywords(incident.threats.okta.description, [incident.context.attackerIP, incident.context.targetUser]) }}
            />
          </div>
          
          <div className="bg-arbitium-dark/50 border border-orange-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Cloud className="w-5 h-5 text-orange-400" />
              <span className="font-semibold text-orange-400">AWS</span>
              <span className={`ml-auto px-2 py-0.5 text-xs rounded ${getSeverityColor(incident.threats.aws.severity)}`}>
                {incident.threats.aws.severity}
              </span>
            </div>
            <div className="text-white font-medium mb-1">{incident.threats.aws.title}</div>
            <div 
              className="text-sm text-gray-400"
              dangerouslySetInnerHTML={{ __html: highlightKeywords(incident.threats.aws.description, [incident.context.attackerIP, incident.context.instanceId]) }}
            />
          </div>
          
          <div className="bg-arbitium-dark/50 border border-red-500/30 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Monitor className="w-5 h-5 text-red-400" />
              <span className="font-semibold text-red-400">CrowdStrike</span>
              <span className={`ml-auto px-2 py-0.5 text-xs rounded ${getSeverityColor(incident.threats.crowdstrike.severity)}`}>
                {incident.threats.crowdstrike.severity}
              </span>
            </div>
            <div className="text-white font-medium mb-1">{incident.threats.crowdstrike.title}</div>
            <div 
              className="text-sm text-gray-400"
              dangerouslySetInnerHTML={{ __html: highlightKeywords(incident.threats.crowdstrike.description, [incident.context.hostname, incident.context.targetUser]) }}
            />
          </div>

          {!analysisComplete && !executed && (
            <button 
              onClick={runAnalysis}
              disabled={analyzing}
              className="w-full px-6 py-3 bg-gradient-to-r from-arbitium-primary to-arbitium-accent text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-arbitium-primary/20 disabled:opacity-50"
            >
              {analyzing ? (
                <><Loader className="w-5 h-5 animate-spin" /> Analyzing Correlation...</>
              ) : (
                <><Play className="w-5 h-5" /> Run Cross-Domain Analysis</>
              )}
            </button>
          )}
          
          {analysisComplete && !executed && (
            <button 
              onClick={executeRemediation}
              disabled={executing}
              className="w-full px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-medium rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition-all shadow-lg shadow-green-500/20 disabled:opacity-50"
            >
              {executing ? (
                <><Loader className="w-5 h-5 animate-spin" /> Executing Across 3 Platforms...</>
              ) : (
                <><Zap className="w-5 h-5" /> Execute Cross-Domain Remediation</>
              )}
            </button>
          )}
          
          {executed && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle className="w-5 h-5" />
                <span className="font-semibold">Threat Contained</span>
              </div>
              <p className="text-green-300 text-sm mt-1">
                All {incident.remediation.okta.actions.length + incident.remediation.aws.actions.length + incident.remediation.crowdstrike.actions.length} remediation actions executed successfully across 3 platforms.
              </p>
            </div>
          )}
        </div>

        <div className="col-span-2 bg-arbitium-dark/50 border border-arbitium-primary/10 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <Clock className="w-5 h-5 text-arbitium-accent" />
            Unified Attack Timeline
          </h3>
          
          <div className="space-y-3 max-h-[600px] overflow-y-auto pr-2">
            {timeline.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>Run analysis to see the attack timeline</p>
              </div>
            ) : (
              timeline.map((event, index) => (
                <div 
                  key={event.id}
                  className={`relative pl-8 pb-4 border-l-2 ${
                    event.status === 'complete' 
                      ? 'border-green-500/50' 
                      : event.status === 'in-progress' 
                        ? 'border-yellow-500/50 animate-pulse' 
                        : 'border-gray-600/50'
                  }`}
                >
                  <div className={`absolute -left-2.5 top-0 w-5 h-5 rounded-full flex items-center justify-center ${
                    event.status === 'complete'
                      ? 'bg-green-500'
                      : event.status === 'in-progress'
                        ? 'bg-yellow-500 animate-pulse'
                        : 'bg-gray-600'
                  }`}>
                    {event.status === 'complete' ? (
                      <CheckCircle className="w-3 h-3 text-white" />
                    ) : event.status === 'in-progress' ? (
                      <Loader className="w-3 h-3 text-white animate-spin" />
                    ) : (
                      <div className="w-2 h-2 bg-gray-400 rounded-full" />
                    )}
                  </div>
                  
                  <div className={`bg-arbitium-darker/50 rounded-xl p-4 border ${
                    event.type === 'detection' 
                      ? 'border-red-500/20' 
                      : event.type === 'analysis' 
                        ? 'border-purple-500/20' 
                        : 'border-green-500/20'
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium flex items-center gap-1 border ${getSourceColor(event.source)}`}>
                        {getSourceIcon(event.source)}
                        {event.source.toUpperCase()}
                      </span>
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        event.type === 'detection' ? 'bg-red-500/20 text-red-400' :
                        event.type === 'analysis' ? 'bg-purple-500/20 text-purple-400' :
                        'bg-green-500/20 text-green-400'
                      }`}>
                        {event.type}
                      </span>
                      <span className="text-xs text-gray-500 ml-auto">
                        {event.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                    
                    <div className="text-white font-medium mb-1">{event.title}</div>
                    <div 
                      className="text-sm text-gray-400"
                      dangerouslySetInnerHTML={{ __html: highlightKeywords(event.description, event.keywords) }}
                    />
                    
                    {event.command && (
                      <div className="mt-3 bg-black/50 rounded-lg p-3 font-mono text-xs">
                        <div className="text-gray-500 mb-1">$ Command:</div>
                        <div 
                          className="text-cyan-400 break-all"
                          dangerouslySetInnerHTML={{ __html: highlightKeywords(event.command, event.keywords) }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {showReport && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-arbitium-dark border border-arbitium-primary/30 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="sticky top-0 bg-arbitium-dark border-b border-arbitium-primary/20 p-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">Cross-Domain Incident Report</h2>
              <button onClick={() => setShowReport(false)} className="p-2 text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Executive Summary</h3>
                <p className="text-gray-300">
                  A coordinated {incident.scenario.name.toLowerCase()} attack was detected and automatically remediated 
                  by Arbitium across Okta, AWS, and CrowdStrike platforms. The attack originated from IP {incident.context.attackerIP} ({incident.context.country}) 
                  targeting user {incident.context.targetUser}. Arbitium's cross-domain correlation engine identified the attack with {incident.correlation.confidence}% confidence 
                  and executed {incident.remediation.okta.actions.length + incident.remediation.aws.actions.length + incident.remediation.crowdstrike.actions.length} remediation 
                  actions in under 5 seconds.
                </p>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">MITRE ATT&CK Mapping</h3>
                <div className="bg-arbitium-darker/50 rounded-xl p-4">
                  <div className="text-sm text-gray-400 mb-1">Tactics:</div>
                  <div className="flex gap-2 mb-3">
                    {incident.mitre.tactics.map((t, i) => (
                      <span key={i} className="px-2 py-1 bg-purple-500/20 text-purple-400 rounded text-sm">{t}</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-400 mb-1">Techniques:</div>
                  <div className="flex flex-wrap gap-2">
                    {incident.mitre.techniques.map((t, i) => (
                      <span key={i} className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-sm">{t}</span>
                    ))}
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold text-white mb-2">Remediation Actions Taken</h3>
                <div className="space-y-2">
                  {[...incident.remediation.okta.actions, ...incident.remediation.aws.actions, ...incident.remediation.crowdstrike.actions].map((action, i) => (
                    <div key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 shrink-0" />
                      <span className="text-gray-300">{action.action}: {action.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrossDomainIncidentDetail;
